import { AUTH_MODE, getIdToken, relogin } from '../auth/msal';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

// dev 暫定: VITE_DEV_USER_EMAIL を X-User-Email ヘッダで送る（AUTH_MODE=header 時）。
const DEV_USER_EMAIL = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_USER_EMAIL ?? '')
  : '';

// 認証ヘッダ: msal モードは id_token を Bearer 送信、header モードは X-User-Email。
// forceRefresh=true でトークンを強制更新する（401 リトライ用）。
async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  if (AUTH_MODE === 'msal') {
    const token = await getIdToken(forceRefresh);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  return DEV_USER_EMAIL ? { 'X-User-Email': DEV_USER_EMAIL } : {};
}

/**
 * 認証ヘッダ付きで fetch する。401 が返ったら1回だけトークンを強制更新して
 * 再試行し、それでも401なら再ログインへ送る。
 * （id_token の期限切れで master-sync/users 系が 401 になるのを自動回復する）
 */
async function fetchWithAuth(
  url: string,
  init: (headers: Record<string, string>) => RequestInit,
): Promise<Response> {
  const res = await fetch(url, init(await authHeaders()));
  if (res.status !== 401 || AUTH_MODE !== 'msal') return res;

  const refreshed = await authHeaders(true);
  // トークンを取り直せなかった場合は getIdToken 側でログインへリダイレクト済み。
  if (!refreshed.Authorization) return res;
  const retry = await fetch(url, init(refreshed));
  if (retry.status === 401) await relogin();
  return retry;
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}: ${body}`);
  }
}

/**
 * API が返した本文から、利用者に見せる文言（error フィールド）を取り出す。
 * JSON でない・error が無い場合は null（呼び出し側で既定の文言に倒す）。
 */
export function parseApiErrorMessage(e: ApiError): string | null {
  try {
    const msg = (JSON.parse(e.body) as { error?: unknown }).error;
    return typeof msg === 'string' && msg ? msg : null;
  } catch {
    return null;
  }
}

/**
 * 応答を受け取る前に通信が切れたか（ブラウザの "Failed to fetch" 等）。
 * 大量明細のOCRは処理が長く、Azure が約230秒で通信を切るとこれになる。
 */
export function isNetworkError(e: unknown): boolean {
  return e instanceof TypeError && /fetch|network|load failed/i.test(e.message);
}

/** 通信は切れたがサーバーで処理が続いている可能性があるか（切断・ゲートウェイのタイムアウト） */
export function isProbablyStillProcessing(e: unknown): boolean {
  return isNetworkError(e) || (e instanceof ApiError && e.status >= 502 && e.status <= 504);
}

/**
 * 例外を利用者に見せる文言にする。ブラウザやAPIの生のエラー文
 * （"Failed to fetch" / "API 500: {...}"）をそのまま出さない。
 */
export function userErrorMessage(e: unknown): string {
  if (isNetworkError(e)) {
    return 'サーバーとの通信が途中で切れました。時間をおいてもう一度お試しください。';
  }
  if (e instanceof ApiError) {
    const msg = parseApiErrorMessage(e);
    if (msg) return msg;
    if (e.status >= 502 && e.status <= 504) {
      return 'サーバーの応答に時間がかかっています。時間をおいてもう一度お試しください。';
    }
    return `処理に失敗しました（エラーコード ${e.status}）。`;
  }
  return (e as Error)?.message || '処理に失敗しました。';
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(`${BASE}${path}`, (headers) => ({ headers }));
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const payload = JSON.stringify(body);
  const res = await fetchWithAuth(`${BASE}${path}`, (headers) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: payload,
  }));
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export async function apiPostForm<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetchWithAuth(`${BASE}${path}`, (headers) => ({
    method: 'POST',
    headers,
    body: formData,
  }));
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export function apiDownloadUrl(path: string): string {
  return `${BASE}${path}`;
}
